/**
 * Modifier Selection Rules — the single source of truth for "what may the
 * guest actually pick from this group?"
 *
 * ## Why this exists
 *
 * A modifier group carries EIGHT independently-editable selection fields
 * (`selectionRequirement`, `minSelection`, `maxSelection`,
 * `allowMultipleSelection`, `allowDuplicateSelection`,
 * `maxDuplicatesPerModifier`, `minUniqueSelection`, `maxUniqueSelection`) and
 * a per-item `ModifierGroupReference` may override five of them. Nothing in
 * the management UI couples any of it, so the fields routinely contradict each
 * other — and reading any ONE of them alone is how a surface ends up ignoring
 * the configuration the operator saved:
 *
 * - `ModifierGroupModal`'s form defaults write `minSelection: 0` on EVERY
 *   group it saves. Any check shaped `minSelection ?? (isRequired ? 1 : 0)`
 *   therefore collapses to a floor of 0, and `selectionRequirement: Required`
 *   enforces nothing. (`ModifierGroup.validateSelection` had exactly this,
 *   spelled `count < this.minSelection` → `count < 0` → never true.)
 * - `allowMultipleSelection` alone cannot decide multi-select: a group set to
 *   `maxSelection: 3`, or to `allowDuplicateSelection`, or to
 *   `maxDuplicatesPerModifier: 2`, is multi-select whatever that toggle says.
 *   Treating it as single-select caps the group at 1 and makes duplicates
 *   structurally unreachable.
 *
 * ## Who must use it
 *
 * Every surface that lets a human choose modifiers, so they cannot drift:
 * the online-ordering `ItemModal`, the AI assistant's cart builder, the
 * `ModifierGroup` model's own `isRequired`/`isMultiSelect`/`validateSelection`,
 * and — ported, not shared, because they are Dart — the POS
 * (`ModifierGroup`'s effective-rule getters) and the mobile orders app
 * (`GroupSelectionConfig.resolve`). Four copies of these rules already drifted
 * apart once; keep the ports 1:1 with this file.
 *
 * Lives in `models` because that is the lowest layer — `common`, `core`,
 * `client` and every app can reach it, and it depends on nothing.
 */

/** The subset of a modifier group this resolver reads. */
export interface ModifierSelectionRuleSource {
  readonly selectionRequirement?: "Optional" | "Required" | null;
  readonly minSelection?: number | null;
  readonly maxSelection?: number | null;
  readonly allowMultipleSelection?: boolean | null;
  readonly allowDuplicateSelection?: boolean | null;
  readonly maxDuplicatesPerModifier?: number | null;
  readonly minUniqueSelection?: number | null;
  readonly maxUniqueSelection?: number | null;
}

/**
 * The subset of a per-item `ModifierGroupReference` this resolver reads.
 *
 * A reference value WINS over the group's when present — it is the
 * parent-specific configuration. Note the reference carries no unique- or
 * duplicate-cap overrides; those always come from the group.
 */
export interface ModifierSelectionRuleOverride {
  readonly isRequired?: boolean | null;
  readonly minSelection?: number | null;
  readonly maxSelection?: number | null;
  readonly allowMultipleSelection?: boolean | null;
  readonly allowDuplicateSelection?: boolean | null;
}

/** The reconciled rules. Every consumer reads these and only these. */
export interface ModifierSelectionRules {
  /** Must the guest choose something here? */
  readonly isRequired: boolean;
  /** Minimum TOTAL selections. */
  readonly minSelection: number;
  /** Maximum TOTAL selections; `null` means no cap. */
  readonly maxSelection: number | null;
  /** May more than one option be selected? */
  readonly allowMultiple: boolean;
  /** May the SAME option be chosen more than once? */
  readonly allowDuplicate: boolean;
  /** Cap on repeats of one option; `null` = bounded only by [maxSelection]. */
  readonly maxPerModifier: number | null;
  /** Minimum DISTINCT options; `null` = none beyond [minSelection]. */
  readonly minUnique: number | null;
  /** Maximum DISTINCT options; `null` = none beyond [maxSelection]. */
  readonly maxUnique: number | null;
  /** Exactly one option at a time — tapping another REPLACES the current. */
  readonly isSingleSelect: boolean;
}

const positive = (value: number | null | undefined): number | null =>
  typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : null;

const nonNegative = (value: number | null | undefined): number | null =>
  typeof value === "number" && Number.isFinite(value) && value >= 0
    ? value
    : null;

/**
 * Reconcile a group's selection fields (optionally overlaid with a per-item
 * reference) into one coherent answer.
 *
 * Pure. Order of resolution:
 *  1. reference value, when present, beats the group's;
 *  2. `Required` raises the minimum to at least 1, and a unique-floor raises
 *     it further — this is what makes `Required` mean something against the
 *     `minSelection: 0` every management save writes;
 *  3. any field that is only meaningful above one selection implies multi;
 *  4. an explicit cap below the resolved floor is a contradiction the modal
 *     will happily save across two edit sessions — the FLOOR wins, so a
 *     required group always stays satisfiable;
 *  5. with no explicit cap, single-select means a cap of 1 and multi means
 *     no cap.
 */
export function resolveModifierSelectionRules(
  group: ModifierSelectionRuleSource,
  reference?: ModifierSelectionRuleOverride | null,
): ModifierSelectionRules {
  const refMin = nonNegative(reference?.minSelection);
  const refMax = positive(reference?.maxSelection);
  const groupMin = nonNegative(group.minSelection);
  const groupMax = positive(group.maxSelection);

  const explicitMax = refMax ?? groupMax;
  const minUnique = positive(group.minUniqueSelection);
  const maxUnique = positive(group.maxUniqueSelection);
  const maxPerModifierRaw = positive(group.maxDuplicatesPerModifier);

  // 2. The floor.
  const declaredRequired =
    reference?.isRequired === true ||
    group.selectionRequirement === "Required" ||
    (refMin ?? 0) > 0 ||
    (groupMin ?? 0) > 0;

  let minSelection = refMin ?? groupMin ?? 0;
  if (declaredRequired && minSelection < 1) minSelection = 1;
  if (minUnique !== null && minUnique > minSelection) minSelection = minUnique;

  // 3. Anything only meaningful above one selection implies multi.
  const allowDuplicate =
    reference?.allowDuplicateSelection === true ||
    group.allowDuplicateSelection === true ||
    maxPerModifierRaw !== null && maxPerModifierRaw > 1;

  const allowMultiple =
    reference?.allowMultipleSelection === true ||
    group.allowMultipleSelection === true ||
    allowDuplicate ||
    (explicitMax !== null && explicitMax > 1) ||
    (maxUnique !== null && maxUnique > 1) ||
    minSelection > 1;

  // 4 + 5. The cap.
  let maxSelection: number | null;
  if (explicitMax !== null) {
    maxSelection = explicitMax < minSelection ? minSelection : explicitMax;
  } else {
    maxSelection = allowMultiple ? null : 1;
  }

  return {
    isRequired: minSelection > 0,
    minSelection,
    maxSelection,
    allowMultiple,
    allowDuplicate,
    // Duplicates off means one of each, by definition.
    maxPerModifier: allowDuplicate ? maxPerModifierRaw : 1,
    minUnique,
    maxUnique,
    isSingleSelect: maxSelection === 1,
  };
}

/** One violation of a group's selection rules. */
export interface ModifierSelectionViolation {
  readonly code:
    | "below_minimum"
    | "above_maximum"
    | "duplicates_not_allowed"
    | "above_duplicate_limit"
    | "below_unique_minimum"
    | "above_unique_maximum";
  readonly message: string;
}

/**
 * Check a selection against reconciled [rules].
 *
 * `selectedModifierIds` repeats an id once per instance — `[7, 7, 9]` is "two
 * of 7, one of 9". Counting distinct ids instead of instances is how a
 * quantity-3 modifier reads as a single selection and slips past every cap.
 *
 * Returns the FIRST violation, or `null` when the selection is valid.
 */
export function validateModifierSelection(
  selectedModifierIds: readonly number[],
  rules: ModifierSelectionRules,
): ModifierSelectionViolation | null {
  const total = selectedModifierIds.length;
  const uniqueCount = new Set(selectedModifierIds).size;

  if (total < rules.minSelection) {
    return {
      code: "below_minimum",
      message:
        rules.minSelection === 1
          ? "Choose an option"
          : `Choose at least ${rules.minSelection}`,
    };
  }

  if (rules.maxSelection !== null && total > rules.maxSelection) {
    return {
      code: "above_maximum",
      message: `Choose at most ${rules.maxSelection}`,
    };
  }

  if (!rules.allowDuplicate && uniqueCount !== total) {
    return {
      code: "duplicates_not_allowed",
      message: "Each option may be chosen only once",
    };
  }

  if (rules.maxPerModifier !== null) {
    const counts = new Map<number, number>();
    for (const id of selectedModifierIds) {
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    for (const count of counts.values()) {
      if (count > rules.maxPerModifier) {
        return {
          code: "above_duplicate_limit",
          message: `Choose at most ${rules.maxPerModifier} of any one option`,
        };
      }
    }
  }

  if (rules.minUnique !== null && uniqueCount < rules.minUnique) {
    return {
      code: "below_unique_minimum",
      message: `Choose at least ${rules.minUnique} different option(s)`,
    };
  }

  if (rules.maxUnique !== null && uniqueCount > rules.maxUnique) {
    return {
      code: "above_unique_maximum",
      message: `Choose at most ${rules.maxUnique} different option(s)`,
    };
  }

  return null;
}
